// ─────────────────────────────────────────────────────────────────
// Glass-i — Product Creation Studio constants + pure helpers
// (Stage 6B.8A-3).
//
// All vocabularies mirror the backend constants:
//   - inventory_items     (Stage 6B.8A-2B)
//   - item_glass_specs    (Stage 6B.8A-2C)
//   - item_profile_specs  (Stage 6B.8A-2D)
//   - item_hardware_specs (Stage 6B.8A-2E)
//   - item_jobwork_specs  (Stage 6B.8A-2F)
//   - ecommerce_product_profiles (Stage 6B.8A-2G)
//   - inventory_item_images      (Stage 6B.8A-2H)
//
// The helpers in this file are pure (no DOM, no React, no axios). They
// are unit-tested by __tests__/productStudioHelpers.test.js using
// node:test — no jsdom required.
// ─────────────────────────────────────────────────────────────────

// ── Core inventory_items ─────────────────────────────────────────
export const ITEM_TYPES = Object.freeze([
  'glass', 'raw_material', 'consumable',
  'service', 'finished_good', 'trading_good'
]);

export const TAXABILITY_TYPES = Object.freeze([
  'taxable', 'exempt', 'nil_rated', 'non_gst', 'zero_rated'
]);

export const ITEM_STATUSES = Object.freeze([
  'active', 'inactive', 'discontinued'
]);

export const INPUT_TYPES = Object.freeze(['MM', 'OTH']);

export const MEASUREMENT_MODES = Object.freeze([
  'qty_based', 'area_based', 'length_based',
  'volume_based', 'weight_based', 'service_based'
]);

export const PRICING_METHODS = Object.freeze([
  'qty_based', 'area_based', 'length_based',
  'volume_based', 'weight_based', 'fixed_rate'
]);

// ── Glass extension ──────────────────────────────────────────────
export const GLASS_TYPES = Object.freeze([
  'annealed', 'toughened', 'laminated', 'dgu', 'heat_strengthened',
  'mirror', 'lacquered', 'frosted', 'other'
]);
export const GLASS_FAMILIES = Object.freeze([
  'clear', 'tinted', 'reflective', 'coated', 'mirror',
  'frosted', 'laminated', 'insulated', 'other'
]);
export const GLASS_SURFACE_TYPES = Object.freeze([
  'plain', 'frosted', 'etched', 'reflective', 'coated', 'textured', 'other'
]);
export const GLASS_SHAPE_TYPES = Object.freeze([
  'rectangle', 'square', 'circle', 'oval', 'arch', 'custom'
]);
export const GLASS_PROCESS_TYPES = Object.freeze([
  'only_cutting', 'rg', 'cep', 'rg_cep',
  'toughened_rg', 'toughened_cep', 'toughened_rg_cep',
  'laminated', 'dgu', 'custom'
]);

// ── Profile extension ────────────────────────────────────────────
export const PROFILE_MATERIALS = Object.freeze([
  'aluminium', 'upvc', 'steel', 'stainless_steel',
  'pvc', 'rubber', 'composite', 'other'
]);
export const PROFILE_USAGES = Object.freeze([
  'window', 'door', 'partition', 'railing', 'shower',
  'facade', 'general', 'other'
]);
export const PROFILE_POSITIONS = Object.freeze([
  'frame', 'shutter', 'mullion', 'transom', 'track', 'channel',
  'beading', 'handle', 'reinforcement', 'gasket', 'other'
]);
export const PROFILE_FINISH_TYPES = Object.freeze([
  'mill_finish', 'powder_coated', 'anodized', 'wooden_finish',
  'painted', 'polished', 'matte', 'glossy', 'other'
]);
export const PROFILE_COATING_TYPES = Object.freeze([
  'none', 'powder_coated', 'anodized', 'pvdf', 'painted', 'other'
]);

// ── Hardware extension ───────────────────────────────────────────
export const HARDWARE_TYPES = Object.freeze([
  'handle', 'hinge', 'lock', 'patch_fitting', 'spider_fitting',
  'roller', 'wheel', 'floor_spring', 'door_closer', 'tower_bolt',
  'screw', 'fastener', 'bracket', 'clamp', 'connector',
  'gasket', 'sealant', 'tape', 'setting_block', 'corner_cleat',
  'channel_accessory', 'railing_hardware', 'shower_fitting',
  'partition_fitting', 'facade_hardware', 'other'
]);
export const HARDWARE_CATEGORIES = Object.freeze([
  'glass_hardware', 'aluminium_hardware', 'upvc_hardware',
  'shower_hardware', 'door_hardware', 'window_hardware',
  'facade_hardware', 'partition_hardware', 'railing_hardware',
  'consumable', 'fastener', 'other'
]);
export const HARDWARE_USAGES = Object.freeze([
  'door', 'window', 'shower', 'partition', 'facade', 'railing',
  'sliding', 'casement', 'fixed', 'general', 'other'
]);
export const HARDWARE_POSITIONS = Object.freeze([
  'top', 'bottom', 'left', 'right', 'side', 'center', 'corner',
  'floor', 'wall', 'frame', 'shutter', 'track', 'channel',
  'glass', 'profile', 'general', 'other'
]);
export const HARDWARE_MATERIALS = Object.freeze([
  'stainless_steel', 'aluminium', 'brass', 'zinc', 'steel',
  'mild_steel', 'plastic', 'nylon', 'rubber', 'epdm', 'silicone',
  'pvc', 'composite', 'other'
]);
export const HARDWARE_FINISH_TYPES = Object.freeze([
  'polished', 'matte', 'glossy', 'satin', 'brushed', 'powder_coated',
  'anodized', 'chrome', 'black', 'white', 'wooden_finish',
  'painted', 'raw', 'other'
]);
export const HARDWARE_COATING_TYPES = Object.freeze([
  'none', 'powder_coated', 'anodized', 'chrome_plated',
  'zinc_plated', 'painted', 'pvdf', 'other'
]);

// ── Jobwork extension ────────────────────────────────────────────
export const JOBWORK_TYPES = Object.freeze([
  'glass_processing', 'profile_processing', 'hardware_fitting',
  'installation', 'transport', 'handling', 'labour', 'repair',
  'replacement', 'packing', 'other'
]);
export const JOBWORK_CATEGORIES = Object.freeze([
  'cutting', 'polishing', 'drilling', 'toughening', 'lamination',
  'dgu', 'printing', 'frosting', 'beveling', 'bending',
  'installation', 'transport', 'handling', 'labour', 'other'
]);
export const JOBWORK_PROCESS_TYPES = Object.freeze([
  'only_cutting', 'shape_cutting', 'cnc_cutting',
  'rg', 'cep', 'rg_cep', 'hole', 'notch',
  'bevelling', 'frosting', 'sandblasting', 'etching',
  'uv_printing', 'ceramic_printing',
  'toughening', 'heat_soak', 'lamination', 'dgu',
  'bending', 'mirror_processing',
  'installation', 'transport', 'handling', 'packing', 'custom'
]);
export const JOBWORK_DIRECTIONS = Object.freeze([
  'inhouse', 'outsource', 'both'
]);
export const JOBWORK_MATERIAL_MODES = Object.freeze([
  'customer_material', 'own_material', 'supplier_material',
  'mixed', 'not_applicable'
]);
export const JOBWORK_CHARGE_BASES = Object.freeze([
  'sqft', 'sqm', 'rft', 'meter', 'piece', 'kg', 'hour', 'day', 'trip',
  'fixed', 'percentage', 'other'
]);
export const JOBWORK_RATE_BASES = Object.freeze([
  'per_sqft', 'per_sqm', 'per_rft', 'per_meter', 'per_piece', 'per_kg',
  'per_hour', 'per_day', 'per_trip', 'fixed', 'percentage', 'other'
]);

// ── Ecommerce ────────────────────────────────────────────────────
export const PUBLISH_STATUSES = Object.freeze([
  'draft', 'review', 'published', 'archived'
]);
export const VISIBILITIES = Object.freeze([
  'hidden', 'public', 'private', 'unlisted'
]);
// Mirror of backend SLUG_PATTERN — kept as a string so it can be re-
// compiled per call (Joi-style regex .test() is fine to reuse, but
// keeping the source visible avoids accidental drift).
export const SLUG_PATTERN_SRC = '^[a-z0-9]+(?:-[a-z0-9]+)*$';

// ── Images ───────────────────────────────────────────────────────
export const STORAGE_PROVIDERS = Object.freeze([
  'digitalocean_spaces', 'local', 'external_url', 'other'
]);
export const IMAGE_ROLES = Object.freeze([
  'primary', 'gallery', 'technical', 'drawing', 'label', 'packaging',
  'color_sample', 'finish_sample', 'thumbnail', 'og_image', 'other'
]);
export const IMAGE_STATUSES = Object.freeze([
  'active', 'inactive', 'archived'
]);
export const ALLOWED_IMAGE_MIME_TYPES = Object.freeze([
  'image/jpeg', 'image/png', 'image/webp', 'image/gif', 'image/svg+xml'
]);
export const ALLOWED_IMAGE_FILE_EXTS = Object.freeze([
  'jpg', 'jpeg', 'png', 'webp', 'gif', 'svg'
]);

// ── Stepper definition ───────────────────────────────────────────
export const STUDIO_STEP_KEYS = Object.freeze([
  'basic', 'classification', 'gst_pricing',
  'technical', 'ecommerce', 'images', 'review'
]);

export const STUDIO_STEPS = Object.freeze([
  { key: 'basic',          title: 'Basic',          subtitle: 'Identity & description' },
  { key: 'classification', title: 'Classification', subtitle: 'Type, family & flags' },
  { key: 'gst_pricing',    title: 'GST & Pricing',  subtitle: 'UOM, HSN, rates' },
  { key: 'technical',      title: 'Technical',      subtitle: 'Glass / Profile / Hardware / Jobwork' },
  { key: 'ecommerce',      title: 'Ecommerce',      subtitle: 'Catalog & SEO (optional)' },
  { key: 'images',         title: 'Images',         subtitle: 'Gallery & primary image' },
  { key: 'review',         title: 'Review',         subtitle: 'Summary & save' }
]);

// ── Section toggle definition (Step 4) ───────────────────────────
export const TECHNICAL_SECTIONS = Object.freeze([
  { key: 'glass',    label: 'Glass Details',    helperText: 'Use for sheet, toughened, laminated, DGU, mirror items.' },
  { key: 'profile',  label: 'Profile Details',  helperText: 'Use for aluminium / UPVC / slim / shutter / frame / track profiles.' },
  { key: 'hardware', label: 'Hardware Details', helperText: 'Use for handles, hinges, locks, patch fittings, spider fittings, rollers.' },
  { key: 'jobwork',  label: 'Jobwork Details',  helperText: 'Use for chargeable processes — toughening, lamination, polishing, drilling, transport, installation, labour.' }
]);

// ─────────────────────────────────────────────────────────────────
// PURE HELPERS — exercised by node:test in __tests__
// ─────────────────────────────────────────────────────────────────

// Reject the two taxability values that are explicitly out of scope
// for Item Master per the spec ("service" / "rcm"). The backend enum
// does not include them either; this is an early frontend guard.
export function isAllowedTaxability(value) {
  if (value === null || value === undefined || value === '') return true;
  const v = String(value).toLowerCase();
  if (v === 'service' || v === 'rcm') return false;
  return TAXABILITY_TYPES.includes(v);
}

// Slugs accepted by ecommerce_product_profiles. The backend Joi schema
// validates the same way. Empty / null is treated as "absent" — the
// step-5 publish guard separately requires it when publish_status is
// 'published'. NOTE: this validator does NOT lowercase the input — the
// Form's `normalize` prop handles the auto-lowercase UX; the validator
// must reflect the raw value so an uppercase paste is rejected.
export function isValidSlug(value) {
  if (value === null || value === undefined || value === '') return true;
  const re = new RegExp(SLUG_PATTERN_SRC);
  return re.test(String(value).trim());
}

// Aadhaar-style or PAN-style validation is out of scope here; this is
// a tiny helper used by the URL fields (canonical / og_image_url) in
// the ecommerce step.
export function isValidHttpUrl(value) {
  if (value === null || value === undefined || value === '') return true;
  try {
    const u = new URL(String(value));
    return u.protocol === 'http:' || u.protocol === 'https:';
  } catch (_e) {
    return false;
  }
}

// Cross-field guard for create — at least one of is_goods / is_service
// must be true. Matches the backend custom() validator. Both flags
// `undefined` defaults to is_goods=true on the backend, so we treat
// `undefined` the same as `true` for is_goods.
export function isGoodsOrServiceComplete(values) {
  if (!values || typeof values !== 'object') return false;
  const g = values.isGoods === undefined ? true  : Boolean(values.isGoods);
  const s = values.isService === undefined ? false : Boolean(values.isService);
  return g === true || s === true;
}

// Self-link guard for source_stock_item_id. DB CHECK prevents the
// row from referring to its own id; we catch the case client-side
// for a friendlier message.
export function isValidSourceStockLink(values, currentItemId) {
  if (!values || !values.sourceStockItemId) return true;
  if (!currentItemId) return true;
  return values.sourceStockItemId !== currentItemId;
}

// When ecommerce.publishStatus = 'published', the backend custom()
// guard requires slug + visibility (public/unlisted) + isVisibleOnline.
// This duplicates only the slug+visibility gate (visibility default
// is 'hidden', so an unset value would fail). The image step shows
// a warning if isVisibleOnline=false.
export function isEcommercePublishable(values) {
  if (!values || typeof values !== 'object') return true;
  if (values.publishStatus !== 'published') return true;
  if (!values.ecommerceSlug || !isValidSlug(values.ecommerceSlug)) return false;
  const vis = values.visibility;
  if (vis !== undefined && vis !== 'public' && vis !== 'unlisted') return false;
  if (values.isVisibleOnline === false) return false;
  return true;
}

// ── Smart section-toggle inference (Step 4) ──────────────────────
//
// Glance the classification flags / category code / material family
// to recommend a default set of technical sections. The user can
// always override via the per-section toggle — this is just the
// initial pick.
//
// Inputs (all optional):
//   itemType:           'glass' | 'raw_material' | 'consumable' | ...
//   materialFamily:     'glass' | 'aluminium' | 'upvc' | 'hardware' | ...
//   isService:          boolean
//   isJobworkItem:      boolean
//   itemCategoryCode:   free-text — pattern-matched
//
// Returns a plain object — the four toggle flags. UI initialises the
// section toggles from this on mount and on classification changes.
export function inferEnabledSections(values) {
  const out = { glass: false, profile: false, hardware: false, jobwork: false };
  if (!values || typeof values !== 'object') return out;

  const itemType       = String(values.itemType || '').toLowerCase();
  const materialFamily = String(values.materialFamily || '').toLowerCase();
  const categoryCode   = String(values.itemCategoryCode || '').toLowerCase();
  const categoryName   = String(values.itemCategoryName || '').toLowerCase();
  const materialName   = String(values.materialTypeName || '').toLowerCase();
  const itemName       = String(values.itemName || '').toLowerCase();

  const blob = `${itemType} ${materialFamily} ${categoryCode} ${categoryName} ${materialName} ${itemName}`;

  if (itemType === 'glass' || /\bglass\b/.test(blob) || materialFamily === 'glass') {
    out.glass = true;
  }
  if (/(profile|aluminium|upvc|frame|shutter|track|channel|beading)/.test(blob)) {
    out.profile = true;
  }
  if (/(hardware|fitting|hinge|handle|lock|roller|patch|spider|fastener|bracket)/.test(blob)) {
    out.hardware = true;
  }
  if (values.isJobworkItem === true || values.isService === true) {
    out.jobwork = true;
  }
  if (/(installation|transport|handling|labour|labor|toughen|lamination|polishing|drilling|jobwork)/.test(blob)) {
    out.jobwork = true;
  }
  return out;
}

// ─────────────────────────────────────────────────────────────────
// PAYLOAD BUILDERS — keep extension keys OUT of the core payload, and
// strip empty strings so optional fields fall back to backend defaults.
// ─────────────────────────────────────────────────────────────────

// Keys accepted by the core inventory_items create / update endpoint.
// Mirrors backend/src/services/erp/masters/items/items.validation.js.
export const CORE_ITEM_KEYS = Object.freeze([
  'itemCode', 'itemName', 'itemDesc', 'stockDesc', 'labelDesc',
  'skucode', 'barcode',
  'itemType', 'itemCategoryId', 'materialTypeId',
  'inputtype', 'measurementMode',
  'hsncode', 'uom', 'defaultUomId', 'defaultUqcCode', 'decimalPrecision',
  'gstRateId', 'gstRatePercent', 'taxabilityType',
  'cessApplicable', 'cessRate',
  'isGoods', 'isService',
  'pricingMethod', 'purchaseRate', 'salesRate',
  // Stage 6B.8A-3G — selling-price band (Sell / Min / Max).
  'minSalesRate', 'maxSalesRate',
  'mrp', 'rateEffectiveFrom',
  'minOrderQty', 'leadTimeDays', 'weightPerUnit', 'grossWeight',
  'reorderLevel', 'reorderQty',
  'isStockItem', 'isPurchasable', 'isSellable',
  'isManufacturable', 'isConsumable', 'isJobworkItem',
  'status', 'isActive', 'remarks',
  'sourceStockItemId',
  // Stage 6B.8A-3D — Brand master FK.
  'brandId'
]);

function stripEmpty(obj) {
  const out = {};
  for (const k of Object.keys(obj)) {
    const v = obj[k];
    if (v === '' || v === undefined) continue;
    out[k] = v;
  }
  return out;
}

export function buildCoreItemPayload(values) {
  if (!values || typeof values !== 'object') return {};
  const out = {};
  for (const k of CORE_ITEM_KEYS) {
    if (Object.prototype.hasOwnProperty.call(values, k)) out[k] = values[k];
  }
  return stripEmpty(out);
}

// Glass spec keys. Stripping is permissive — the backend validates
// only allowed values and silently drops anything unknown.
export const GLASS_SPEC_KEYS = Object.freeze([
  'thicknessMm', 'glassType', 'glassFamily',
  'glassColor', 'tintColor',
  'surfaceType', 'coatingType', 'polishTypeId',
  'edgeWorkType', 'shapeType', 'processType',
  'isToughenable', 'isToughened', 'isLaminated',
  'isDgu', 'isHeatStrengthened', 'isBendGlass',
  'remarks'
]);

export function buildGlassSpecPayload(values) {
  if (!values || typeof values !== 'object') return {};
  const out = {};
  for (const k of GLASS_SPEC_KEYS) {
    if (Object.prototype.hasOwnProperty.call(values, k)) out[k] = values[k];
  }
  return stripEmpty(out);
}

export const PROFILE_SPEC_KEYS = Object.freeze([
  'profileSystem', 'profileMaterial', 'profileSeries',
  'profileCode', 'profileName', 'profileUsage', 'profilePosition',
  'profileColor', 'finishType', 'coatingType', 'surfaceFinish',
  'lengthMm', 'sectionWidthMm', 'sectionHeightMm',
  'wallThicknessMm', 'thicknessMm', 'weightPerMeter',
  'brand', 'modelNo', 'manufacturerCode',
  'isAluminium', 'isUpvc', 'isSlimProfile',
  'isFrameProfile', 'isShutterProfile', 'isTrackProfile',
  'isChannelProfile', 'isBeadingProfile', 'isPartitionProfile',
  'remarks'
]);

export function buildProfileSpecPayload(values) {
  if (!values || typeof values !== 'object') return {};
  const out = {};
  for (const k of PROFILE_SPEC_KEYS) {
    if (Object.prototype.hasOwnProperty.call(values, k)) out[k] = values[k];
  }
  return stripEmpty(out);
}

export const HARDWARE_SPEC_KEYS = Object.freeze([
  'hardwareType', 'hardwareCategory', 'hardwareSubcategory',
  'hardwareUsage', 'hardwarePosition',
  'hardwareCode', 'hardwareName',
  'material', 'finishType', 'coatingType', 'color', 'surfaceFinish',
  'brand', 'modelNo', 'manufacturerCode', 'supplierPartNo',
  'sizeLabel', 'lengthMm', 'widthMm', 'heightMm', 'diameterMm',
  'thicknessMm', 'weightPerUnit', 'loadCapacityKg',
  'compatibleSystem', 'compatibleProfile',
  'compatibleGlassThicknessMinMm', 'compatibleGlassThicknessMaxMm',
  'isLockable', 'isMovable', 'isVisible',
  'isConsumable', 'isFastener',
  'isGlassHardware', 'isProfileHardware',
  'isShowerHardware', 'isDoorHardware',
  'isWindowHardware', 'isFacadeHardware',
  'isPartitionHardware',
  'remarks'
]);

export function buildHardwareSpecPayload(values) {
  if (!values || typeof values !== 'object') return {};
  const out = {};
  for (const k of HARDWARE_SPEC_KEYS) {
    if (Object.prototype.hasOwnProperty.call(values, k)) out[k] = values[k];
  }
  return stripEmpty(out);
}

export const JOBWORK_SPEC_KEYS = Object.freeze([
  'jobworkType', 'jobworkCategory', 'jobworkSubcategory',
  'jobworkProcessType', 'jobworkDirection', 'jobworkMaterialMode',
  'jobworkCode', 'jobworkName',
  'chargeBasis', 'rateBasis',
  'minimumCharge', 'minimumQty',
  'wastagePercent', 'processLossPercent',
  'minThicknessMm', 'maxThicknessMm',
  'minAreaSqft', 'maxAreaSqft',
  'minAreaSqm', 'maxAreaSqm',
  'minLengthMm', 'maxLengthMm',
  'minWidthMm',  'maxWidthMm',
  'standardLeadTimeDays', 'standardProcessTimeMinutes',
  'requiresMachine', 'requiresQc', 'requiresOutsource',
  'isInternalProcess', 'isCustomerChargeable',
  'isSupplierBillable', 'isInstallationService',
  'isTransportService', 'isLabourService',
  'compatibleGlassType', 'compatibleMaterialType', 'compatibleProcessGroup',
  'remarks'
]);

export function buildJobworkSpecPayload(values) {
  if (!values || typeof values !== 'object') return {};
  const out = {};
  for (const k of JOBWORK_SPEC_KEYS) {
    if (Object.prototype.hasOwnProperty.call(values, k)) out[k] = values[k];
  }
  return stripEmpty(out);
}

export const ECOMMERCE_PROFILE_KEYS = Object.freeze([
  'ecommerceSlug', 'canonicalUrl',
  'metaTitle', 'metaDescription', 'metaKeywords',
  'ecommerceTitle', 'shortDescription', 'longDescription',
  'productHighlights', 'technicalSpecs',
  'careInstructions', 'warrantyText',
  'ecommerceCategory', 'ecommerceSubcategory',
  'productCollection', 'productTags',
  'publishStatus', 'visibility',
  'isVisibleOnline', 'isFeatured', 'isNewArrival',
  'isBestSeller', 'isCustomizable',
  'allowOnlineEnquiry', 'allowOnlineOrder',
  'displayOrder', 'badgeText', 'searchBoost',
  'ogTitle', 'ogDescription', 'ogImageUrl'
]);

export function buildEcommerceProfilePayload(values) {
  if (!values || typeof values !== 'object') return {};
  const out = {};
  for (const k of ECOMMERCE_PROFILE_KEYS) {
    if (Object.prototype.hasOwnProperty.call(values, k)) out[k] = values[k];
  }
  return stripEmpty(out);
}

export const IMAGE_KEYS = Object.freeze([
  'storageProvider', 'bucketName', 'storageKey', 'publicUrl',
  'signedUrlExpiresAt',
  'originalFileName', 'storedFileName',
  'mimeType', 'fileExt', 'fileSizeBytes', 'checksumSha256',
  'imageWidthPx', 'imageHeightPx', 'aspectRatio', 'dominantColor',
  'imageRole', 'altText', 'caption', 'displayOrder',
  'isPrimary', 'isVisibleOnline', 'isVisibleInternal',
  'status'
]);

export function buildImagePayload(values) {
  if (!values || typeof values !== 'object') return {};
  const out = {};
  for (const k of IMAGE_KEYS) {
    if (Object.prototype.hasOwnProperty.call(values, k)) out[k] = values[k];
  }
  return stripEmpty(out);
}

// Helpful labels for the review step. Maps internal enum value → UI
// label (Title Case, no underscores).
export function humaniseEnum(value) {
  if (value === null || value === undefined || value === '') return '';
  return String(value)
    .split('_').map((p) => p ? (p[0].toUpperCase() + p.slice(1)) : '')
    .join(' ');
}
